import { useState, useRef, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { SmilePlus } from "lucide-react";
import { cn } from "@/lib/utils";

const REACTIONS = ["❤️", "👍", "💡", "😂", "🙏", "😢", "🔥", "🤔", "👎", "⭐"];

export function ReactionBar({ conversationId, messageIndex, existingReactions, onReactionChange }) {
  const { token } = useAuth();
  const [showPicker, setShowPicker] = useState(false);
  const [reactions, setReactions] = useState(existingReactions || {});
  const [animating, setAnimating] = useState(null);
  const pickerRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    setReactions(existingReactions || {});
  }, [existingReactions]);

  useEffect(() => {
    const handleClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    if (showPicker) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showPicker]);

  const toggleReaction = async (emoji) => {
    // Optimistic update
    const newReactions = { ...reactions };
    if (emoji in newReactions) {
      delete newReactions[emoji];
    } else {
      newReactions[emoji] = new Date().toISOString();
      setAnimating(emoji);
      setTimeout(() => setAnimating(null), 600);
    }
    setReactions(newReactions);
    setShowPicker(false);
    if (onReactionChange) onReactionChange(messageIndex, newReactions);

    try {
      await axios.post(`${API}/chat/reaction`, {
        conversation_id: conversationId,
        message_index: messageIndex,
        emoji,
      }, { headers });
    } catch (err) {
      // Revert on error
      setReactions(existingReactions || {});
    }
  };

  const reactionKeys = Object.keys(reactions);

  return (
    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
      {/* Existing reactions */}
      {reactionKeys.map(emoji => (
        <button
          key={emoji}
          data-testid={`reaction-${messageIndex}-${emoji}`}
          onClick={() => toggleReaction(emoji)}
          className={cn(
            "inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-sm transition-all hover:scale-110",
            "bg-primary/10 border border-primary/20",
            animating === emoji && "animate-bounce"
          )}
        >
          <span className="text-base leading-none">{emoji}</span>
        </button>
      ))}

      {/* Add reaction button */}
      <div className="relative" ref={pickerRef}>
        <button
          data-testid={`add-reaction-${messageIndex}`}
          onClick={() => setShowPicker(!showPicker)}
          className={cn(
            "inline-flex items-center justify-center w-7 h-7 rounded-full transition-all",
            showPicker
              ? "bg-primary/10 text-primary"
              : "opacity-0 group-hover:opacity-100 hover:bg-muted/50 text-muted-foreground"
          )}
        >
          <SmilePlus className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>

        {/* Picker */}
        {showPicker && (
          <div className="absolute bottom-9 left-0 z-50 bg-card border border-border/50 rounded-2xl shadow-xl p-2 flex gap-1 animate-fade-in">
            {REACTIONS.map(emoji => (
              <button
                key={emoji}
                data-testid={`picker-${messageIndex}-${emoji}`}
                onClick={() => toggleReaction(emoji)}
                className={cn(
                  "w-9 h-9 rounded-xl flex items-center justify-center text-lg hover:bg-muted/50 transition-all hover:scale-125",
                  emoji in reactions && "bg-primary/10 ring-1 ring-primary/30"
                )}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
